import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Loader2, Search as SearchIcon, ShieldCheck, ShieldOff } from "lucide-react";
import { toast } from "sonner";
import { formatApiError } from "@/lib/errors";

/**
 * EditDistributorAccessDialog
 *
 * Lets an admin or agency_user choose which subset of the agency's manufacturers
 * a given distributor user can see. Stores the explicit list via
 *   PUT /api/distributor-users/{user_id}/manufacturers
 *
 * "All access" = null/unset → distributor sees every manufacturer the agency
 * carries. Any toggle into a partial set switches to explicit-list mode.
 */
export default function EditDistributorAccessDialog({ open, onOpenChange, distributorUser, onSaved }) {
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [agencyManufacturers, setAgencyManufacturers] = useState([]); // [{id, name}]
  const [allAccess, setAllAccess] = useState(true);
  const [selected, setSelected] = useState(new Set());
  const [query, setQuery] = useState("");

  useEffect(() => {
    if (!open || !distributorUser) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const res = await api.get(
          `/distributor-users/${distributorUser.id}/manufacturers`
        );
        if (cancelled) return;
        const restricted = !!res.data.restricted;
        setAllAccess(!restricted);
        setSelected(new Set(restricted ? res.data.manufacturer_ids || [] : []));
        // Load full agency-wide manufacturer list for the checklist
        const agencyId = distributorUser.agency_id;
        if (agencyId) {
          const linesRes = await api.get(`/rep-agencies/${agencyId}/lines`, {
            params: { include_inactive: false },
          });
          if (cancelled) return;
          const list = (linesRes.data || [])
            .map((ln) => ln.manufacturer)
            .filter(Boolean)
            .sort((a, b) => (a.name || "").localeCompare(b.name || ""));
          setAgencyManufacturers(list);
        } else {
          setAgencyManufacturers([]);
        }
      } catch (err) {
        toast.error(formatApiError(err, "Could not load manufacturer access"));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, distributorUser]);

  const toggle = (id) => {
    setAllAccess(false);
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const switchToAllAccess = () => {
    setAllAccess(true);
    setSelected(new Set());
  };

  const switchToCustomAccess = () => {
    setAllAccess(false);
    // Default to selecting all currently-available manufacturers as a starting
    // point — user can then uncheck the ones they want to deny.
    setSelected(new Set(agencyManufacturers.map((m) => m.id)));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = allAccess
        ? { manufacturer_ids: null }
        : { manufacturer_ids: Array.from(selected) };
      await api.put(
        `/distributor-users/${distributorUser.id}/manufacturers`,
        payload
      );
      toast.success("Manufacturer access updated");
      onSaved?.();
      onOpenChange(false);
    } catch (err) {
      toast.error(formatApiError(err, "Could not update access"));
    } finally {
      setSaving(false);
    }
  };

  const filtered = query
    ? agencyManufacturers.filter((m) =>
        m.name.toLowerCase().includes(query.toLowerCase())
      )
    : agencyManufacturers;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        className="max-w-lg rounded-sm"
        data-testid="edit-distributor-access-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-display tracking-tight">
            Manufacturer access
          </DialogTitle>
          <DialogDescription>
            Choose which manufacturers <strong>{distributorUser?.name}</strong>{" "}
            ({distributorUser?.distributor_location || distributorUser?.email}) can see
            in search.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="py-8 text-center text-sm text-muted-foreground">
            <Loader2 className="w-4 h-4 animate-spin inline mr-2" />
            Loading…
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 gap-2" data-testid="access-mode-toggle">
              <button
                type="button"
                onClick={switchToAllAccess}
                className={`text-left p-3 rounded-sm border transition-colors ${
                  allAccess
                    ? "border-slate-900 bg-slate-900 text-white"
                    : "border-border bg-white text-slate-900 hover:border-slate-400"
                }`}
                data-testid="access-mode-all"
              >
                <div className="flex items-center gap-2">
                  <ShieldCheck className="w-4 h-4" />
                  <span className="font-semibold text-sm">Full access</span>
                </div>
                <p className="text-xs mt-1 opacity-80">
                  See every manufacturer the agency carries.
                </p>
              </button>
              <button
                type="button"
                onClick={switchToCustomAccess}
                className={`text-left p-3 rounded-sm border transition-colors ${
                  !allAccess
                    ? "border-amber-500 bg-amber-50 text-slate-900"
                    : "border-border bg-white text-slate-900 hover:border-amber-300"
                }`}
                data-testid="access-mode-custom"
              >
                <div className="flex items-center gap-2">
                  <ShieldOff className="w-4 h-4" />
                  <span className="font-semibold text-sm">Custom list</span>
                </div>
                <p className="text-xs mt-1 opacity-80">
                  Restrict to specific manufacturers (territory-based).
                </p>
              </button>
            </div>

            {!allAccess && (
              <div className="space-y-3" data-testid="access-checklist">
                <div className="relative">
                  <SearchIcon className="w-4 h-4 absolute left-2.5 top-2.5 text-muted-foreground" />
                  <Input
                    placeholder="Filter manufacturers…"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    className="rounded-sm pl-8"
                    data-testid="access-filter-input"
                  />
                </div>
                <div className="text-[11px] uppercase tracking-widest text-muted-foreground font-mono-data flex items-center justify-between">
                  <span>
                    {selected.size} of {agencyManufacturers.length} selected
                  </span>
                  <button
                    type="button"
                    onClick={() => setSelected(new Set(agencyManufacturers.map((m) => m.id)))}
                    className="text-amber-700 hover:text-amber-800 normal-case tracking-normal underline-offset-2 hover:underline"
                  >
                    Select all
                  </button>
                </div>
                <div className="max-h-64 overflow-y-auto border border-border rounded-sm divide-y divide-border">
                  {filtered.length === 0 ? (
                    <div className="py-6 text-center text-xs text-muted-foreground italic">
                      No manufacturers match.
                    </div>
                  ) : (
                    filtered.map((m) => (
                      <label
                        key={m.id}
                        htmlFor={`mfr-${m.id}`}
                        className="flex items-center gap-3 px-3 py-2 cursor-pointer hover:bg-slate-50"
                        data-testid={`access-row-${m.id}`}
                      >
                        <Checkbox
                          id={`mfr-${m.id}`}
                          checked={selected.has(m.id)}
                          onCheckedChange={() => toggle(m.id)}
                        />
                        <span className="text-sm font-medium text-slate-900 flex-1">
                          {m.name}
                        </span>
                      </label>
                    ))
                  )}
                </div>
              </div>
            )}
          </>
        )}

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            className="rounded-sm"
            disabled={saving}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={saving || loading}
            className="rounded-sm bg-slate-900 hover:bg-slate-800"
            data-testid="access-save-btn"
          >
            {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
            Save access
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
